import { useEffect, useRef } from 'react';
import { useTracker } from '@archipro-website/tracker';

import { useAddToCart } from '@modules/ecommerce/hook/use-add-to-cart';
import type { CartItem } from '@modules/ecommerce/type/cart-item';
import { useCartPopupState } from '~/modules/ecommerce/context/CartPopupContext';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { toast } from 'sonner';

interface UseAddToCartWithTrackingOptions {
    onItemAdded?: (item: CartItem | null) => void;
    /**
     * Open the cart popup / top-nav cart menu after a successful add
     * (default). Mobile surfaces pass false and confirm in their own sheet.
     */
    openCartPopup?: boolean;
}

/**
 * Add the selected variant to the cart with the full legacy side-effect set:
 * cart popup open, error toast, and the `AddToCartClick` / `AddToCart` tracker
 * events. Shared by the
 * legacy purchase button group and the revamp's inline mobile purchase flow so
 * the behaviour stays single-source. Must render inside
 * `VariantSelectionProvider`.
 */
export const useAddToCartWithTracking = ({
    onItemAdded,
    openCartPopup = true,
}: UseAddToCartWithTrackingOptions = {}) => {
    const { quantity, selectedVariant } = useVariantSelectionState();
    const { onChange: onCartPopupChange } = useCartPopupState();
    const [addToCart, fetcher] = useAddToCart(false, {
        openCartMenu: openCartPopup,
    });
    const tracker = useTracker();

    /** Adds the current selection to the cart and opens the cart popup. */
    const addSelectedToCart = () => {
        if (!selectedVariant) return;
        // The press, before the cart answers; `AddToCart` below is the
        // add the cart confirmed.
        void tracker.log('AddToCartClick', {
            url: new URL(window.location.href),
        });
        addToCart(selectedVariant, quantity);
        if (openCartPopup) onCartPopupChange(true);
    };

    // The fetcher keeps its `data` after settling (idle + data), so the effect
    // re-runs whenever a dependency identity changes (e.g. the inline
    // `onItemAdded` recreated each render by the desktop drawer). Track the last
    // processed result so each completed add fires its tracking + callback
    // exactly once instead of duplicating AddToCart events on re-render.
    const processedData = useRef<unknown>(null);

    useEffect(() => {
        if (fetcherIsDone(fetcher) && fetcher.data) {
            if (processedData.current === fetcher.data) return;
            processedData.current = fetcher.data;
            if ('error' in fetcher.data) {
                toast.error(fetcher.data.message || 'Unable to add to cart');
            } else {
                const lastAddedItem =
                    fetcher.data?.addToShoppingCart.LastAddedItems[0];
                if (lastAddedItem) {
                    tracker.log('AddToCart', {
                        url: new URL(window.location.href),
                        data: {
                            // format: CartItemID
                            ExtraData: `${lastAddedItem.ID || 0}`,
                        },
                    });
                    onItemAdded?.({
                        ...lastAddedItem,
                    });
                }
            }
        }
    }, [fetcher, tracker, onItemAdded]);

    return { addSelectedToCart, isLoading: fetcher.state !== 'idle' };
};
